import { forwardRef, type KeyboardEvent } from 'react';
import { Search, ChevronUp, ChevronDown, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Props = {
  query: string;
  onQueryChange: (q: string) => void;
  total: number;
  /** Posizione corrente 1-based (0 se non ci sono risultati). */
  current: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  className?: string;
};

/**
 * Barra "trova nella pagina": input + contatore + frecce su/giù + chiudi.
 * Tastiera: Enter = successivo, Shift+Enter = precedente, Esc = chiudi.
 * Il ref è inoltrato all'`<input>` così il chiamante può dargli il focus.
 */
export const FindBar = forwardRef<HTMLInputElement, Props>(function FindBar(
  { query, onQueryChange, total, current, onPrev, onNext, onClose, className },
  ref,
) {
  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) onPrev();
      else onNext();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  const hasQuery = query.trim() !== '';

  return (
    <div
      role="search"
      className={cn(
        'flex items-center gap-1 rounded-md border border-(--color-border) bg-(--color-background) p-1 shadow-sm',
        className,
      )}
    >
      <div className="relative flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-(--color-muted-foreground)" />
        <Input
          ref={ref}
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Cerca nell'articolo…"
          aria-label="Cerca nell'articolo"
          className="h-9 border-0 pl-8 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>

      <span className="whitespace-nowrap px-1 text-xs tabular-nums text-(--color-muted-foreground)">
        {!hasQuery ? '' : total === 0 ? 'Nessun risultato' : `${current}/${total}`}
      </span>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={onPrev}
        disabled={total === 0}
        aria-label="Risultato precedente"
      >
        <ChevronUp className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={onNext}
        disabled={total === 0}
        aria-label="Risultato successivo"
      >
        <ChevronDown className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={onClose}
        aria-label="Chiudi ricerca"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
});
